import AppIcon from "../components/AppIcon";
import { useCallback, useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import useMoveTo from "../constants/navigation";
import { data, useParams } from "react-router";
import {
    ref,
    onValue,
    off,
    push,
    set,
    update,
    remove,
} from "firebase/database";
import { LuPhone as BsTelephone, LuEllipsisVertical as BsThreeDotsVertical } from "react-icons/lu";
import { LuVideo as FiVideo } from "react-icons/lu";
import {
    compressImage,
    convertHeicToJpg,
    formatDate,
    formatTime,
    getChatId,
    handleDownloadImage,
} from "../utils/utils";
import { useUser } from "../contexts/UserContext";
import {
    ref as storageRef,
    uploadBytesResumable,
    getDownloadURL,
} from "firebase/storage";
import { database, functions, storage } from "../constants/firebase";
import BlockUserBottomSheet from "../components/BlockUserBottomSheet";
import ReportUserBottomSheet from "../components/ReportUserBottomSheet";
import { httpsCallable } from "firebase/functions";
import { useCall } from "../contexts/CallContext";
import { ROUTES } from "../App";
import useKeyboardOpen from "../hooks/useKeyboardOpen";
import TypingAnimation from "../components/TypingAnimation";
import ReactionBox from "../components/ReactionBox";
import MessageBubble from "../components/MessageBubble";
import MessageInput from "../components/MessageInput";
import RecordingPopup from "../components/RecordingPopup";
import { useChatMessages } from "../hooks/useChatMessages";
import { useChatScroll } from "../hooks/useChatScroll";
import { useMessageActions } from "../hooks/useMessageActions";
import { useTypingStatus } from "../hooks/useTypingStatus";
import { useRecording } from "../hooks/useRecording";
import { formatGroupedMessages, groupMessagesByDate } from "../utils/chatUtils";

const PrivateUserChat = () => {
    const navigate = useMoveTo();
    const { userId } = useParams();
    const { currentUser } = useUser();
    const [chatUser, setChatUser] = useState(null);
    const [newMessage, setNewMessage] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const textareaRef = useRef(null);
    const [isBlockSheetOpen, setIsBlockSheetOpen] = useState(false);
    const [isBlockedByMe, setIsBlockedByMe] = useState(false);
    const [isBlockedByOther, setIsBlockedByOther] = useState(false);
    const [updateUi, setUpdateUi] = useState(false);
    const isBlocked = isBlockedByMe || isBlockedByOther;
    const [isReportSheetOpen, setIsReportSheetOpen] = useState(false);
    const [isReported, setIsReported] = useState(false);
    const isSelfChat = userId === currentUser.uid;
    const chatId = getChatId(currentUser.uid, userId);

    const [activeCallListener, setActiveCallListener] = useState(null);
    const [callStartTime, setCallStartTime] = useState(null);
    const [callTimerInterval, setCallTimerInterval] = useState(null);

    // Custom hooks
    const { messages, loading, deleteMessage, addReaction, removeReaction } =
        useChatMessages('private', chatId, currentUser);

    const { scrollContainerRef } = useChatScroll(messages);

    const {
        selectionMode,
        setSelectionMode,
        selectedMessage,
        setSelectedMessage,
        selectedMessageId,
        setSelectedMessageId,
        reactionPosition,
        editingMessage,
        editMessageText,
        cancelEdit,
        setEditMessageText,
        replyingTo,
        swipeProgress,
        reactionBoxRef,
        longPressTimeout,
        longPressTriggered,
        isScrollingRef,
        openReactionBox,
        closeReactionBox,
        handleEditMessage,
        handleReplyToMessage,
        clearReply,
        handleSwipeStart,
        handleSwipeMove,
        handleSwipeEnd
    } = useMessageActions(currentUser);

    const { isTyping, updateTypingStatus } = useTypingStatus('private', chatId, currentUser, userId);

    const {
        isRecording,
        recordingPopup,
        startRecording,
        stopRecording,
        cancelRecording,
        uploadAudio
    } = useRecording('private', chatId, currentUser, chatUser);


    const [isRequestChat, setIsRequestChat] = useState(false);
    const [isFreeUserRestricted, setIsFreeUserRestricted] = useState(false);

    const isRestricted = isBlocked || isReported || isFreeUserRestricted;
    const {
        setCallStatus,
        callObject,

        // context states
        setIsOutgoingCall,
        setActiveCallData,
        setCallRoomUrl,
        setCallType,
        setUserId,
    } = useCall();
    const [isCallLoading, setIsCallLoading] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const isKeyboardOpen = useKeyboardOpen();

    const [loadedImages, setLoadedImages] = useState({});
    const previousMessageCount = useRef(0);

    const shouldAutoScroll = useRef(true);
    const lastScrollPosition = useRef(0);
    const hasScrolledToBottom = useRef(false);

    const typingTimeoutRef = useRef(null);


    const scrollToBottom = useCallback((behavior = "auto") => {
        if (!scrollContainerRef.current) return;
        const container = scrollContainerRef.current;
        container.scrollTo({
            top: container.scrollHeight,
            behavior
        });
    }, []);


    const autoResizeTextarea = (el) => {
        el.style.height = "auto";

        const maxHeight = 112; // ~5 lines
        if (el.scrollHeight > maxHeight) {
            el.style.height = maxHeight + "px";
            el.style.overflowY = "auto"; // ✅ allow scrolling
        } else {
            el.style.height = el.scrollHeight + "px";
            el.style.overflowY = "hidden";
        }
    };

    const resetTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }
    };


    useEffect(() => {
        const handleClickOutside = (event) => {
            // If clicked on delete button, ignore
            // If we're in editing mode, don't clear selection
            if (editingMessage) return;
            if (event.button === 2) return;

            // Clear selection if:
            if (!editingMessage) {
                // Clicked on a message
                setSelectionMode(false);
                setSelectedMessage(null);
            }

            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            // Close reaction box
            if (
                reactionBoxRef.current &&
                !reactionBoxRef.current.contains(event.target)
            ) {
                setSelectedMessageId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [selectedMessage, editingMessage]);

    useEffect(() => {
        if (!userId) return;

        const chatId = getChatId(currentUser.uid, userId);
        const messagesRef = ref(database, `privateChats/${chatId}`);

        onValue(messagesRef, (snapshot) => {
            if (snapshot.exists()) {
                const msgs = Object.entries(snapshot.val()).map(([id, val]) => ({
                    id,
                    ...val,
                }));

                // 👇 Mark all unseen messages from the OTHER user as seen
                msgs.forEach((msg) => {
                    if (msg.senderId === userId && !msg.seen) {
                        set(ref(database, `privateChats/${chatId}/${msg.id}/seen`), true);
                    }
                });
            } else {
            }
        });

        return () => off(messagesRef);
    }, [userId, currentUser.uid]);

    useEffect(() => {
        if (!currentUser?.uid || !userId || !chatUser) return;

        // Check if this is a request chat
        // If current user is Host, check their requestChats for this userId
        // If current user is Free, check the chatUser's (Host's) requestChats for currentUser.uid
        let requestRef;

        if (currentUser.role === "Host") {
            requestRef = ref(
                database,
                `Users/${currentUser.uid}/requestChats/${userId}`,
            );
        } else {
            requestRef = ref(
                database,
                `Users/${userId}/requestChats/${currentUser.uid}`,
            );
        }

        onValue(requestRef, (snapshot) => {
            setIsRequestChat(snapshot.exists());
            setIsFreeUserRestricted(
                currentUser.role === "Free" &&
                chatUser.role === "Host" &&
                snapshot.exists() &&
                messages.length === 1,
            );
        });

        return () => off(requestRef);
    }, [currentUser.uid, userId, chatUser, currentUser.role, messages.length]);

    useEffect(() => {
        if (!currentUser?.uid || !userId) return;

        const reportRef = ref(
            database,
            `Users/${currentUser.uid}/reportedUsers/${userId}`,
        );
        const unsubReport = onValue(reportRef, (snapshot) => {
            setIsReported(snapshot.exists());
        });

        return () => unsubReport();
    }, [currentUser?.uid, userId]);

    useEffect(() => {
        if (!currentUser?.uid || !userId) return;

        // Check if I blocked them
        const myBlockRef = ref(
            database,
            `Users/${currentUser.uid}/blockedUsers/${userId}`,
        );
        const unsubMyBlock = onValue(myBlockRef, (snapshot) => {
            setIsBlockedByMe(snapshot.exists());
        });

        // Check if they blocked me
        const theirBlockRef = ref(
            database,
            `Users/${userId}/blockedUsers/${currentUser.uid}`,
        );
        const unsubTheirBlock = onValue(theirBlockRef, (snapshot) => {
            setIsBlockedByOther(snapshot.exists());
        });

        return () => {
            unsubMyBlock(); // ✅ correctly removes listener
            unsubTheirBlock();
        };
    }, [currentUser?.uid, userId, updateUi]);

    useEffect(() => {
        if (messages.length > 0 && chatUser && !hasScrolledToBottom.current) {
            // Small delay ensures the DOM is fully rendered
            setTimeout(() => {
                scrollToBottom("auto");
                hasScrolledToBottom.current = true;
                shouldAutoScroll.current = true;
            }, 100);
        }
    }, [messages.length, chatUser, scrollToBottom]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

            if (isNearBottom) {
                shouldAutoScroll.current = true;
            } else if (scrollTop < lastScrollPosition.current) {
                // user scrolled up – disable auto‑scroll
                shouldAutoScroll.current = false;
            }

            lastScrollPosition.current = scrollTop;
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (messages.length > previousMessageCount.current && shouldAutoScroll.current) {
            scrollToBottom("smooth");
        }
        previousMessageCount.current = messages.length;
    }, [messages, scrollToBottom]);

    useEffect(() => {
        if (!userId) return;

        const userRef = ref(database, `Users/${userId}`);

        const unsubscribe = onValue(userRef, (snapshot) => {
            if (snapshot.exists()) {
                setChatUser(snapshot.val());
            } else {
                setChatUser(null);
            }
        });

        return () => {
            off(userRef);
            unsubscribe();
        };
    }, [userId]);

    const formatMessages = (messages) => {
        if (!messages.length) return [];

        const formattedMessages = [];
        let lastSenderId = null;

        messages.forEach((message, index) => {
            const showHeader =
                lastSenderId !== message.senderId ||
                (index > 0 && messages[index - 1].senderId !== message.senderId);

            formattedMessages.push({
                ...message,
                showHeader,
            });

            lastSenderId = message.senderId;
        });

        return formattedMessages;
    };

    // Group messages by date
    const groupedMessages = groupMessagesByDate(messages, formatDate);

    // Format each day's messages
    const formattedGroupedMessages = formatGroupedMessages(groupedMessages, formatMessages);


    const handleSendMessage = async () => {
        if (!newMessage.trim() || !chatUser) return;

        // If this is the first message from a free user to a host, add to request chats
        if (
            currentUser.role === "Free" &&
            chatUser.role === "Host" &&
            messages.length === 0
        ) {
            const requestRef = ref(
                database,
                `Users/${userId}/requestChats/${currentUser.uid}`,
            );
            await set(requestRef, true);
        }

        const chatId = getChatId(currentUser.uid, userId);
        const messagesRef = ref(database, `privateChats/${chatId}`);
        const newMessageRef = push(messagesRef);

        const messageData = {
            text: newMessage,
            senderId: currentUser.uid,
            timestamp: Date.now(),
            seen: false,
        };

        // Add reply data if replying to a message
        if (replyingTo) {
            messageData.replyTo = {
                messageId: replyingTo.id,
                senderId: replyingTo.senderId,
                // senderName: replyingTo.senderName,
            };

            // Only add properties that exist (not undefined)
            if (replyingTo.text) {
                messageData.replyTo.text = replyingTo.text;
            }

            if (replyingTo.imageUrl) {
                messageData.replyTo.imageUrl = replyingTo.imageUrl;
            }

            if (replyingTo.audioUrl) {
                messageData.replyTo.audioUrl = replyingTo.audioUrl;
            }
        }

        await set(newMessageRef, messageData);


        setNewMessage("");
        clearReply();
        // ✅ RESET TEXTAREA HEIGHT TO DEFAULT
        resetTextareaHeight();
    };

    const handleAcceptChat = async () => {
        if (!currentUser?.uid || !userId) return;

        // Remove from request chats - from the Host's requestChats
        const requestRef = ref(
            database,
            `Users/${currentUser.uid}/requestChats/${userId}`,
        );
        await set(requestRef, null);

        setIsRequestChat(false);
    };

    const handleBlockFromRequest = async () => {
        if (!currentUser?.uid || !userId) return;

        // Remove from request chats - from the Host's requestChats
        const requestRef = ref(
            database,
            `Users/${currentUser.uid}/requestChats/${userId}`,
        );
        await set(requestRef, null);

        // Block the user
        const blockRef = ref(
            database,
            `Users/${currentUser.uid}/blockedUsers/${userId}`,
        );
        await set(blockRef, true);

        setIsBlockedByMe(true);
        setIsRequestChat(false);
    };

    const handleImageUpload = async (file) => {
        if (!file || !currentUser || !userId) return;

        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
            "image/heic",
            "image/heif"
        ];

        if (!allowedTypes.includes(file.type)) {
            window.showToast("Unsupported image format", "error");
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const chatId = getChatId(currentUser.uid, userId);

            let processedFile = file;

            if (file.type === "image/heic" || file.type === "image/heif") {
                processedFile = await convertHeicToJpg(file);
            } else {
                processedFile = await compressImage(file);
            }

            const filename = `${Date.now()}_${processedFile.name}`;
            const imageRef = storageRef(
                storage,
                `privateChats/${chatId}/${filename}`,
            );

            const uploadTask = uploadBytesResumable(imageRef, processedFile);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress =
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Error uploading image:", error);
                    setIsUploading(false);
                },
                async () => {
                    const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                    const messagesRef = ref(database, `privateChats/${chatId}`);
                    const newMessageRef = push(messagesRef);

                    // If this is the first message from a free user to a host, add to request chats
                    if (
                        currentUser.role === "Free" &&
                        chatUser.role === "Host" &&
                        messages.length === 0
                    ) {
                        const requestRef = ref(
                            database,
                            `Users/${userId}/requestChats/${currentUser.uid}`,
                        );
                        await set(requestRef, true);
                    }

                    await set(newMessageRef, {
                        imageUrl,
                        senderId: currentUser.uid,
                        timestamp: Date.now(),
                        seen: false,
                    });

                    setIsUploading(false);
                    setUploadProgress(0);
                },
            );
        } catch (error) {
            console.error("Error starting upload:", error);
            setIsUploading(false);
        }
    };


    const handleReportClick = async ({ messageId, rating, reason }) => {
        try {
            // ✅ Require at least 1 chat message
            if (messages.length === 0) {
                window.showToast(
                    "⚠️ You can only report users you’ve chatted with.",
                    "error",
                );
                return;
            }

            // ✅ If no messageId provided, fallback to the last message
            if (!messageId) {
                messageId = messages[messages.length - 1].id;
            }

            // 1️⃣ Save report in global "Reports"
            const reportRef = push(ref(database, "Reports"));
            await set(reportRef, {
                reporterId: currentUser.uid,
                reporterEmail: currentUser.email,
                reportedId: chatUser.uid,
                reportedEmail: chatUser.email,
                messageId,
                rating,
                reason,
                timestamp: Date.now(),
            });

            // 2️⃣ Save under my reportedUsers
            const myReportRef = ref(
                database,
                `Users/${currentUser.uid}/reportedUsers/${chatUser.uid}`,
            );
            await set(myReportRef, true);

            // 3️⃣ Block them automatically
            const myBlockRef = ref(
                database,
                `Users/${currentUser.uid}/blockedUsers/${chatUser.uid}`,
            );
            await set(myBlockRef, true);

            // 4️⃣ Send email
            const sendReportEmail = httpsCallable(functions, "sendReportEmail");
            await sendReportEmail({
                reporterEmail: currentUser.email,
                reportedEmail: chatUser.email,
                messageId,
                rating,
                reason,
                timestamp: Date.now(),
            });

            window.showToast(
                "✅ Report submitted. The user has been blocked & chat restricted.",
                "success",
            );
        } catch (err) {
            console.error("❌ Report failed:", err);
            window.showToast("Failed to submit report. Try again.", "error");
        }
    };


    // Start a new outgoing call (caller side)
    const handleOutgoingCall = async (withVideo = false) => {
        try {
            console.log('[CALL] Starting outgoing call...');

            setIsCallLoading(withVideo ? 'video' : 'audio');

            setUserId(currentUser.uid);
            setCallStatus('calling');
            setCallType(withVideo ? 'video' : 'audio');

            if (!window.isInApp) {
                setIsOutgoingCall(true);
            }

            // ─────────────────────────────────────
            // 1. CREATE DAILY ROOM
            // ─────────────────────────────────────
            const resp = await fetch(
                `https://daily-backend-98546189821.us-central1.run.app${ROUTES.CREATE_ROOM_API}`,
            );

            const data = await resp.json();
            const url = data.url;

            setCallRoomUrl(url);

            // ─────────────────────────────────────
            // 2. CREATE callSessions ID
            // ─────────────────────────────────────
            const callId = `${currentUser.uid}_${userId}_${Date.now()}`;

            const callSession = {
                callId,
                from: currentUser.uid,
                to: userId,
                url,
                isVideo: withVideo,
                callType: withVideo ? 'video' : 'audio',
                status: 'ringing',
                handledByApp: !!window.isInApp,
                createdAt: Date.now(),
                participants: {
                    [currentUser.uid]: true,
                    [userId]: true,
                },
                accepted: {
                    [currentUser.uid]: true,
                    [userId]: false,
                },
            };

            // ─────────────────────────────────────
            // 3. SAVE SESSION (SINGLE SOURCE)
            // ─────────────────────────────────────
            await set(ref(database, `callSessions/${callId}`), callSession);

            // ─────────────────────────────────────
            // 4. RN NOTIFICATION
            // ─────────────────────────────────────
            if (window.isInApp) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'OUTGOING_CALL',
                        url,
                        withVideo,
                        callData: callSession,
                    }),
                );
            } else {
                setActiveCallData(callSession);
            }

            // ─────────────────────────────────────
            // 5. LISTEN FOR ACCEPT (FIXED)
            // ─────────────────────────────────────
            const acceptedRef = ref(database, `callSessions/${callId}`);

            const unsubscribe = onValue(acceptedRef, async snap => {
                const callData = snap.val();

                if (callData?.status !== 'connected') return;

                console.log('[CALL] Recipient accepted');

                // ───── RN HANDOFF ─────
                if (window.isInApp) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'CALL_ACCEPTED',
                            url,
                            withVideo,
                            callData
                        }),
                    );
                    return;
                }

                // ───── WEB JOIN ─────
                try {
                    console.log('[CALL] Joining from web...');

                    await callObject.join({ url });

                    callObject.setLocalVideo(withVideo);
                    callObject.setLocalAudio(true);

                    setCallStatus('connected');
                } catch (err) {
                    console.error('❌ Daily join failed:', err);

                    setIsOutgoingCall(false);
                    setActiveCallData(null);
                    setCallStatus('idle');
                }
            });

            return () => off(acceptedRef);

        } catch (err) {
            console.error('❌ Error starting outgoing call:', err);

            setIsOutgoingCall(false);
            setCallStatus('idle');

            // cleanup not needed per-user anymore
        } finally {
            setIsCallLoading('');
        }
    };

    // Add this function to handle updating the edited message
    const handleUpdateMessage = async () => {
        if (!editMessageText.trim() || !currentUser || !userId || !editingMessage) return;

        try {
            const chatId = getChatId(currentUser.uid, userId);
            const messageRef = ref(
                database,
                `privateChats/${chatId}/${editingMessage.id}`
            );

            await update(messageRef, {
                text: editMessageText,
                edited: true,
                editedAt: Date.now()
            });

            // Reset edit state
            cancelEdit();
            setSelectionMode(false);

            // Clear input and reset textarea height
            setNewMessage("");
            resetTextareaHeight();

        } catch (error) {
            console.error("Error updating message:", error);
            window.showToast("Failed to update message", "error");
        }
    };

    const handleGoBack = () => {
        // Try to go back, if there's no history, go to Chats
        if (window.history.length > 2) { // >2 because typically there's at least the current page and the previous
            navigate(-1);
        } else {
            navigate(ROUTES.CHATS);
        }
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center text-gray-500">
                Loading...
            </div>
        );
    }

    if (!chatUser) {
        return (
            <div className="h-screen flex items-center justify-center text-gray-500">
                User not found
            </div>
        );
    }

    return (
        <div className="h-screen bg-white text-black dark:bg-[#23272c] dark:text-white">
            {/* Mobile Header */}
            {/* <MobileHeader /> */}

            <div className="flex">
                {/* Sidebar */}
                <Sidebar />

                {/* Main Content */}
                <main className="flex-1 h-screen flex flex-col bg-white dark:bg-[#23272c] relative">
                    {/* Back + User Info */}
                    <div className="zy-native-chat-header flex items-center space-x-3 md:space-x-8 justify-between md:px-6 px-2 pb-3 pt-4 md:pt-8">
                        {/* Left Side: Back + Avatar + Name */}
                        <div className="flex items-center min-w-0 flex-1">
                            {" "}
                            {/* ✅ min-w-0 allows truncation */}
                            <button
                                onClick={handleGoBack}
                                className="
        flex items-center justify-center
        p-2
        flex-shrink-0
    "
                            >
                                <AppIcon
                                    name="arrowBack"
                                    alt="Back"
                                    className="w-4 h-4 md:w-5 md:h-5 brightness-0 dark:invert"
                                />
                            </button>
                            {/* Avatar */}
                            <img
                                src={
                                    chatUser.profilePic ||
                                    "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"
                                }
                                alt={chatUser.name}
                                className={`w-10 h-10 rounded-full object-cover mx-3 flex-shrink-0 ${isSelfChat ? "cursor-default" : "cursor-pointer"}`}
                                onClick={() =>
                                    isSelfChat
                                        ? null
                                        : navigate(`${ROUTES.VIEW_PROFILE}/${chatUser.uid}`)
                                }
                            />
                            {/* Name + Status */}
                            <div
                                className={`flex flex-col leading-tight min-w-0 ${isSelfChat ? "cursor-default" : "cursor-pointer"}`}
                                onClick={() =>
                                    isSelfChat
                                        ? null
                                        : navigate(`${ROUTES.VIEW_PROFILE}/${chatUser.uid}`)
                                }
                            >
                                {" "}
                                {/* ✅ keeps text from wrapping */}
                                <p
                                    className={`flex items-center gap-2 font-semibold text-base truncate ${chatUser.role === "Host" ? "dark:glow-text-dark glow-text-light" : ""}`}
                                >
                                    {chatUser.name}
                                    {chatUser?.isTeamUser && (
                                        <svg
                                            width="16px"
                                            height="16px"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            stroke="#327691"
                                        >
                                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                            <g
                                                id="SVGRepo_tracerCarrier"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></g>
                                            <g id="SVGRepo_iconCarrier">
                                                <path
                                                    d="M10.5213 2.62368C11.3147 1.75255 12.6853 1.75255 13.4787 2.62368L14.4989 3.74391C14.8998 4.18418 15.4761 4.42288 16.071 4.39508L17.5845 4.32435C18.7614 4.26934 19.7307 5.23857 19.6757 6.41554L19.6049 7.92905C19.5771 8.52388 19.8158 9.10016 20.2561 9.50111L21.3763 10.5213C22.2475 11.3147 22.2475 12.6853 21.3763 13.4787L20.2561 14.4989C19.8158 14.8998 19.5771 15.4761 19.6049 16.071L19.6757 17.5845C19.7307 18.7614 18.7614 19.7307 17.5845 19.6757L16.071 19.6049C15.4761 19.5771 14.8998 19.8158 14.4989 20.2561L13.4787 21.3763C12.6853 22.2475 11.3147 22.2475 10.5213 21.3763L9.50111 20.2561C9.10016 19.8158 8.52388 19.5771 7.92905 19.6049L6.41553 19.6757C5.23857 19.7307 4.26934 18.7614 4.32435 17.5845L4.39508 16.071C4.42288 15.4761 4.18418 14.8998 3.74391 14.4989L2.62368 13.4787C1.75255 12.6853 1.75255 11.3147 2.62368 10.5213L3.74391 9.50111C4.18418 9.10016 4.42288 8.52388 4.39508 7.92905L4.32435 6.41553C4.26934 5.23857 5.23857 4.26934 6.41554 4.32435L7.92905 4.39508C8.52388 4.42288 9.10016 4.18418 9.50111 3.74391L10.5213 2.62368Z"
                                                    stroke="#327691"
                                                    stroke-width="1.5"
                                                ></path>
                                                <path
                                                    d="M9 12L11 14L15 10"
                                                    stroke="#327691"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></path>
                                            </g>
                                        </svg>
                                    )}
                                </p>
                                <p className="text-xs text-gray-500 line-clamp-1">
                                    {isSelfChat
                                        ? "Your private space"
                                        : isTyping
                                            ? <TypingAnimation className="text-gray-500" />
                                            : chatUser.online
                                                ? "Online"
                                                : chatUser.lastSeen
                                                    ? `Last seen at ${formatTime(chatUser.lastSeen)}`
                                                    : "Offline"}
                                </p>
                            </div>
                        </div>

                        {/* Right Side: Call + Video */}
                        {!isSelfChat ? (
                            <div className="flex items-center space-x-1">
                                {/* // 📞 Call + Video buttons when no message is selected */}
                                <button
                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                    onClick={() => handleOutgoingCall(false)}
                                    disabled={
                                        isCallLoading === "audio" || isCallLoading === "video"
                                    }
                                >
                                    {isCallLoading === "audio" ? (
                                        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <BsTelephone className="w-5 h-5 text-gray-800 dark:text-white" />
                                    )}
                                </button>

                                {!window.isInApp && (
                                    <button
                                        className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                        onClick={() => handleOutgoingCall(true)}
                                        disabled={
                                            isCallLoading === "audio" || isCallLoading === "video"
                                        }
                                    >
                                        {isCallLoading === "video" ? (
                                            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <FiVideo className="w-5 h-5 text-gray-800 dark:text-white" />
                                        )}
                                    </button>
                                )}
                                <div className="relative" ref={menuRef}>
                                    <button
                                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                                        className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500/30"
                                    >
                                        <BsThreeDotsVertical className="w-5 h-5 text-gray-800 dark:text-white" />
                                    </button>
                                    {isMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#2A2A2A] rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                                            <ul className="py-1">
                                                {/* ✅ Block/Unblock option only if THEY didn’t block me */}
                                                {!isBlockedByOther && (
                                                    <li>
                                                        <button
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                                setIsBlockSheetOpen(true);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500/30"
                                                        >
                                                            {isBlockedByMe ? "Unblock" : "Block"}
                                                        </button>
                                                    </li>
                                                )}

                                                <li>
                                                    <button
                                                        onClick={() => {
                                                            setIsMenuOpen(false);
                                                            setIsReportSheetOpen(true);
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500/30"
                                                    >
                                                        Report
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Chat Body */}
                    <div
                        ref={scrollContainerRef}
                        className={`pt-4 px-6 py-6 overflow-y-auto flex-1 relative ${isKeyboardOpen ? "pb-20" : "pb-36"} md:pb-0`}
                        style={{
                            backgroundImage: `linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0.9)), url(${IMAGES.chatBg})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            overflowX: "hidden", // 🔥 prevents horizontal movement
                            overflowAnchor: "auto", // 🔥 prevents auto scroll on new messages
                        }}
                    >
                        {messages.length === 0 ? (
                            <div className="flex flex-col items-center mt-8">
                                <div className="max-w-xl w-full border bg-white border-gray-300 dark:border-none dark:bg-[#222] rounded-xl py-6 px-4 flex items-center justify-center mx-6 md:mx-0">
                                    <span className="text-primary text-sm md:text-lg text-center">
                                        Message and calls are end-to-end encrypted. Only people in
                                        this chat can read, listen to, or share them.
                                    </span>
                                </div>
                            </div>
                        ) : (
                            formattedGroupedMessages.map(({ date, messages }) => {
                                const visibleMessages = messages.filter(
                                    (msg) => !(msg.type === 'call' && msg.callStatus !== 'missed')
                                );

                                // 🚀 STOP rendering date completely
                                if (visibleMessages.length === 0) return null;

                                return (
                                    <div key={date} className="mb-6">
                                        {/* Date Header */}
                                        <div className="flex items-center justify-center mb-3">
                                            <div className="bg-white/20 text-white px-8 py-1 rounded-lg text-sm font-medium backdrop-blur-md">
                                                {date}
                                            </div>
                                        </div>

                                        {/* Messages */}
                                        <div className="space-y-4 z-10 relative pb-0 md:pb-0">
                                            {visibleMessages.map((message, index) => {
                                                const isFromCurrentUser = message.senderId === currentUser.uid;
                                                const swipeProgressValue = swipeProgress[message.id] || 0;

                                                return (
                                                    <div
                                                        key={message.id}
                                                        data-message-id={message.id}
                                                        className={`flex relative items-start space-x-4 mt-3 ${isFromCurrentUser ? "justify-end" : "justify-start"
                                                            }`}
                                                        style={{
                                                            zIndex: 5,
                                                        }}
                                                        onContextMenu={(e) => {
                                                            e.preventDefault();
                                                            openReactionBox(message.id, e.clientX, e.clientY, message);
                                                        }}
                                                        onMouseDown={(e) => {
                                                            if (e.button === 2) return;
                                                        }}
                                                        onMouseUp={() =>
                                                            clearTimeout(longPressTimeout.current)
                                                        }
                                                        onMouseLeave={() =>
                                                            clearTimeout(longPressTimeout.current)
                                                        }

                                                        onTouchStart={(e) => {
                                                            handleSwipeStart(e, message.id);
                                                            longPressTimeout.current = setTimeout(() => {
                                                                if (!isScrollingRef.current && Math.abs(swipeProgressValue) < 20) {
                                                                    longPressTriggered.current = true;
                                                                    openReactionBox(message.id, e.touches[0].clientX, e.touches[0].clientY, message);
                                                                }
                                                            }, 600);
                                                        }}
                                                        onTouchMove={(e) => handleSwipeMove(e, message.id, isFromCurrentUser)}
                                                        onTouchEnd={() => {
                                                            clearTimeout(longPressTimeout.current);
                                                            handleSwipeEnd(message.id, () => handleReplyToMessage(message, chatUser));
                                                        }}


                                                    >

                                                        {/* Swipe indicator overlay - this moves instead of the content */}
                                                        {swipeProgressValue !== 0 && (
                                                            <div
                                                                className="absolute inset-0 pointer-events-none"
                                                                style={{
                                                                    transform: `translateX(${swipeProgressValue}px)`,
                                                                    zIndex: 10,
                                                                }}
                                                            >
                                                                <div
                                                                    className={`absolute top-1/2 transform -translate-y-1/2 ${isFromCurrentUser ? 'right-0' : 'left-0'
                                                                        } bg-primary text-white p-2 rounded-full opacity-75`}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <MessageBubble
                                                            message={message}
                                                            isFromCurrentUser={isFromCurrentUser}
                                                            currentUserId={currentUser.uid}
                                                            senderName={chatUser.name}
                                                            senderAvatar={chatUser.profilePic || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef"}
                                                            showHeader={message.showHeader}
                                                            onAvatarClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${message.senderId}`)}
                                                            onImageClick={() => message.imageUrl && setSelectedImage(message.imageUrl)}
                                                            selectionMode={selectionMode}
                                                            selected={selectedMessage?.id === message.id}
                                                            onToggleReaction={(messageId, emojiId, currentReaction) => {
                                                                if (currentReaction === emojiId) {
                                                                    removeReaction(messageId);
                                                                } else {
                                                                    addReaction(messageId, emojiId);
                                                                }
                                                            }}
                                                            loadedImages={loadedImages[message.id]}
                                                            onImageLoad={() => setLoadedImages(prev => ({ ...prev, [message.id]: true }))}
                                                        />

                                                        {/* Reaction Box */}
                                                        {selectedMessageId === message.id && (
                                                            <ReactionBox
                                                                position={reactionPosition}
                                                                isFromCurrentUser={isFromCurrentUser}
                                                                message={message}
                                                                currentUserId={currentUser.uid}
                                                                onReact={(messageId, emoji) => {
                                                                    addReaction(messageId, emoji.id);
                                                                    setSelectedMessage(null);
                                                                    setSelectionMode(false);
                                                                }}
                                                                onReply={(msg) => handleReplyToMessage(msg, chatUser)}
                                                                onEdit={handleEditMessage}
                                                                onDelete={(msgId) => deleteMessage(msgId, message)}
                                                                onClose={closeReactionBox}
                                                                boxRef={reactionBoxRef}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        {isRestricted && isFreeUserRestricted && (
                            <div className="flex flex-col items-center mt-8 mb-14 md:mb-4">
                                <div
                                    className="max-w-md w-full border bg-white border-gray-300 
       dark:border-none dark:bg-[#222] rounded-xl py-3 px-2 
       flex items-center justify-center mx-6 md:mx-0"
                                >
                                    <span className="text-primary text-sm md:text-sm text-center">
                                        {isFreeUserRestricted
                                            ? "Free users can only send one message to Pro members until they accept the chat request."
                                            : isBlockedByMe
                                                ? "You blocked this user. No more messages."
                                                : isBlockedByOther
                                                    ? "This user has blocked you. You cannot send messages."
                                                    : "You reported this user. Messaging is restricted."}
                                    </span>
                                </div>
                            </div>
                        )}
                        {/* 👇 Invisible div to auto scroll */}
                        <div ref={messagesEndRef} />
                    </div>

                    {!isRestricted && (
                        <>
                            {/* Chat Input Bar */}
                            <div
                                className={`border-t dark:border-[#363e46] border-[#D9D9D9]/50 bg-white dark:bg-[#23272c] p-4 pb-6 md:pb-4 fixed md:static ${isKeyboardOpen ? "bottom-0" : "bottom-18"} left-0 right-0 z-10`}
                            >
                                <MessageInput
                                    ref={textareaRef}
                                    value={editingMessage ? editMessageText : newMessage}
                                    onChange={(e) => {
                                        if (editingMessage) {
                                            setEditMessageText(e.target.value);
                                        } else {
                                            setNewMessage(e.target.value);

                                            // Update typing status when user types
                                            if (e.target.value.trim() !== "") {
                                                updateTypingStatus(true);
                                            } else {
                                                updateTypingStatus(false);
                                            }
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            // Insert a newline at the cursor position
                                            const { selectionStart, selectionEnd } = e.target;
                                            const value = editingMessage ? editMessageText : newMessage;
                                            const newValue =
                                                value.substring(0, selectionStart) + "\n" + value.substring(selectionEnd);

                                            if (editingMessage) {
                                                setEditMessageText(newValue);
                                            } else {
                                                setNewMessage(newValue);
                                            }

                                            // Move cursor after the newline
                                            setTimeout(() => {
                                                e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
                                            }, 0);

                                            autoResizeTextarea(e.target);
                                        }
                                    }}
                                    onBlur={() => !editingMessage && updateTypingStatus(false)}
                                    placeholder={replyingTo ? "Reply to message..." : "Type a message..."}
                                    isUploading={isUploading}
                                    onCameraClick={() => fileInputRef.current.click()}
                                    onSend={() => editingMessage ? handleUpdateMessage() : handleSendMessage()}
                                    onRecordStart={startRecording}
                                    isRecording={isRecording}
                                    replyingTo={replyingTo}
                                    onClearReply={clearReply}
                                    currentUserId={currentUser.uid}
                                    senderName={replyingTo?.senderId === currentUser.uid ? "You" : replyingTo?.senderName}
                                    autoResize={autoResizeTextarea}
                                />

                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={(e) => {
                                        if (e.target.files[0]) {
                                            handleImageUpload(e.target.files[0]);
                                        }
                                    }}
                                    accept="image/*"
                                    className="hidden"
                                    disabled={isUploading}
                                />

                                {recordingPopup && (
                                    <RecordingPopup
                                        onCancel={cancelRecording}
                                        onSend={stopRecording}
                                    />
                                )}


                            </div>
                        </>
                    )}

                    {isRequestChat && currentUser.role === "Host" && (
                        <div className="absolute bottom-36 md:bottom-16 left-0 right-0 bg-white dark:bg-[#2A2A2A] p-4 border-t dark:border-[#363e46] border-[#D9D9D9]/50 z-50">
                            <div className="flex justify-center space-x-4">
                                <button
                                    onClick={handleAcceptChat}
                                    className="px-6 py-2 bg-green-500 text-white rounded-full font-semibold"
                                >
                                    Accept Chat
                                </button>
                                <button
                                    onClick={handleBlockFromRequest}
                                    className="px-6 py-2 bg-red-500 text-white rounded-full font-semibold"
                                >
                                    Block User
                                </button>
                            </div>
                        </div>
                    )}
                </main>
            </div>
            {isBlockSheetOpen && (
                <BlockUserBottomSheet
                    user={chatUser}
                    onClose={() => setIsBlockSheetOpen(false)}
                    setUpdateUi={setUpdateUi}
                />
            )}
            {isReportSheetOpen && (
                <ReportUserBottomSheet
                    user={chatUser}
                    currentUser={currentUser}
                    onClose={() => setIsReportSheetOpen(false)}
                    onSubmit={(reportData) =>
                        handleReportClick({
                            ...reportData,
                        })
                    }
                />
            )}
            {selectedImage && (
                <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
                    <img
                        src={selectedImage}
                        alt="Full view"
                        className="max-h-[90%] max-w-[90%] rounded-lg shadow-lg object-contain"
                    />
                    <button
                        onClick={() => setSelectedImage(null)}
                        className="absolute top-5 right-5 text-white text-xl  md:text-3xl font-bold"
                    >
                        ✕
                    </button>
                    {/* Download button */}
                    <button
                        onClick={() => handleDownloadImage(selectedImage)}
                        className="absolute bottom-5 right-5 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/90 transition"
                        title="Download image"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default PrivateUserChat;
